import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { listSessionGroupsOptions, getMyEnrollmentsOptions } from '@/api';
import { SessionGroup } from '@/api/generated/types.gen';
import { PathProgressCard } from '@/components/Learn/PathProgressCard';

export const Route = createFileRoute('/_auth/learn/')({
  component: LearnIndexPage,
});

interface Enrollment {
  session_group_id: string;
  completed_sessions: number;
  total_sessions: number;
  group_cover_image_url?: string | null;
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-2xl border bg-white overflow-hidden">
          <Skeleton className="aspect-[16/9] w-full rounded-none" />
          <div className="p-3.5 space-y-2">
            <Skeleton className="h-4 w-3/4 rounded" />
            <Skeleton className="h-3 w-full rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function LearnIndexPage() {
  const { data: groups, isLoading } = useQuery({
    ...listSessionGroupsOptions(),
    refetchOnWindowFocus: false,
  });

  const { data: enrollments } = useQuery(getMyEnrollmentsOptions());

  const enrollmentMap = new Map(
    (Array.isArray(enrollments) ? (enrollments as Enrollment[]) : []).map((e) => [
      e.session_group_id,
      e,
    ]),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1.5">
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-neutral-900 sm:text-[30px]">
            Learning paths
          </h1>
          <p className="max-w-lg text-[15px] leading-6 text-neutral-600">
            A path is a series of short voice lessons that build on each other. Tell us a topic and
            we&apos;ll make one for you in a minute.
          </p>
        </div>
        <Button
          asChild
          className="h-11 shrink-0 rounded-full bg-neutral-900 px-5 text-[15px] font-medium text-white hover:bg-neutral-800 active:scale-[0.97] sm:h-10">
          <Link to="/chat">Create a path</Link>
        </Button>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : !groups || groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-white px-6 py-16 text-center space-y-3">
          <BookOpen className="h-8 w-8 mx-auto text-muted-foreground/40" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-900">No learning paths yet</p>
            <p className="text-sm text-muted-foreground">
              Describe a topic and we&apos;ll generate a path with interactive sessions.
            </p>
          </div>
          <Button
            asChild
            className="mt-2 h-11 rounded-full bg-neutral-900 px-5 text-[15px] font-medium text-white hover:bg-neutral-800 active:scale-[0.97] sm:h-10">
            <Link to="/chat">Create a path</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group: SessionGroup) => {
            const enrollment = enrollmentMap.get(group.id!);
            const sessionCount = group.session_template_ids?.length ?? 0;
            return (
              <PathProgressCard
                key={group.id}
                path={{
                  id: group.id!,
                  name: group.name,
                  description: group.description,
                  coverImageUrl:
                    enrollment?.group_cover_image_url ?? group.cover_image_url ?? null,
                  totalSessions: enrollment?.total_sessions || sessionCount,
                  completedSessions: enrollment?.completed_sessions ?? 0,
                  sessionGroupId: group.id!,
                  type: group.type,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
